import React, { useEffect, useMemo, useState } from "react";
import { Carousel, Col, Container, Row } from "react-bootstrap";
import MultiCarousel, { ResponsiveType } from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Link } from "react-router-dom";
import Select from "react-select";

import { sanitize } from "dompurify";

import { store } from "../..";
import "../../assets/css/majorybl/pages/home.scss";
import ImageModal from "../../components/ImageModal";
import {
    globalActions,
    selectSetting,
    selectSponsors,
} from "../../features/global/globalSlice";
import {
    leagueManagementActions,
    selectYears,
} from "../../features/leaguemgmt/leagueManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import useBreakpoint from "../../hooks/useBreakpoint";
import { SPONSOR_LOGO_URL, UPLOADED_IMAGE_URL } from "../../lib/globals";
import { MagnifyingGlassIcon } from "../../lib/icons";
import { IImage } from "../../types/shared";
import LeagueStandings from "../Leagues/components/Standings";

export const loader = async () => {
    const settings = store.getState().global.settings;
    const photos = store.getState().global.homePage.photos;
    const leaguesInState = store.getState().leaguemanagement.leagues;
    const years = store.getState().leaguemanagement.years;
    const sponsors = store.getState().global.sponsors;
    const promises = [];

    !photos?.length && promises.push(store.dispatch(globalActions.getPhotos()));
    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));
    !leaguesInState.length &&
        promises.push(store.dispatch(leagueManagementActions.getLeagues()));
    !years && promises.push(store.dispatch(leagueManagementActions.getYears()));
    !sponsors.length &&
        promises.push(store.dispatch(globalActions.getSponsors()));

    await Promise.all(promises);

    return null;
};

const Pagination = ({
    leaguesPerPage,
    totalLeagues,
    currentPage,
    paginate,
}: {
    leaguesPerPage: number;
    totalLeagues: number;
    currentPage: number;
    paginate: (pageNumber: number) => void;
}) => {
    const pageNumbers = [];

    for (let i = 1; i <= Math.ceil(totalLeagues / leaguesPerPage); i++) {
        pageNumbers.push(i);
    }

    return (
        <nav>
            <ul className="pagination">
                {pageNumbers.map(number => (
                    <li
                        key={number}
                        className={`pageItem ${
                            currentPage === number ? `currentPage` : ""
                        }`}
                    >
                        <button
                            onClick={() => paginate(number)}
                            className="pageLink"
                        >
                            {number}
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

const HomePage = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const selectedYear = useAppSelector(
        state => state.global.homePage.selectedYear,
    );
    const selectedSeason = useAppSelector(
        state => state.global.homePage.selectedSeason,
    );
    const selectedAgeLevel = useAppSelector(
        state => state.global.homePage.selectedAgeLevel,
    );
    const selectedLeaguePage =
        useAppSelector(state => state.global.homePage.selectedLeaguePage) || 1;
    const sponsors = useAppSelector(state => selectSponsors(state));
    const photos = useAppSelector(state => state.global.homePage.photos);
    const years = useAppSelector(selectYears);
    const leagueYears =
        years && Array.from(Object.keys(years)).map(y => parseInt(y));

    const breakpoint = useBreakpoint();
    let leaguesPerPage;
    switch (breakpoint) {
        case "xs":
        case "sm":
        case "md":
            leaguesPerPage = 1;
            break;
        case "lg":
            leaguesPerPage = 2;
            break;
        default:
            leaguesPerPage = 3;
    }

    const [modalShow, setModalShow] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<IImage>({
        image: "",
        alt: "",
    });

    const { leagues } = useAppSelector(state => state.leaguemanagement);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );
    const currentSeason = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_season"),
    );
    const homePageBlueBoxContent = useAppSelector(state =>
        selectSetting(state, "majorybl_home_page_blue_box"),
    );
    const homePageRedBoxContent = useAppSelector(state =>
        selectSetting(state, "majorybl_home_page_red_box"),
    );
    const homePageSplitLeftContent = useAppSelector(state =>
        selectSetting(state, "majorybl_home_page_split_left"),
    );
    const homePageSplitRightContent = useAppSelector(state =>
        selectSetting(state, "majorybl_home_page_split_right"),
    );

    const leagueSeasons = useMemo(() => {
        return selectedYear && years?.[selectedYear]
            ? Array.from(Object.keys(years?.[selectedYear]))
            : [];
    }, [selectedYear]);

    const leagueAgeLevels = useMemo(() => {
        return selectedYear && selectedSeason && years?.[selectedYear]
            ? years?.[selectedYear]?.[selectedSeason]
            : [];
    }, [selectedYear, selectedSeason]);

    useEffect(() => {
        const initialYear = leagueYears?.find(
            l => l === parseInt(currentYear?.setting),
        );
        if (!selectedYear) {
            dispatch(globalActions.setSelectedYear(initialYear));
        }
    }, [leagueYears, currentYear, selectedYear, dispatch]);

    useEffect(() => {
        if (selectedYear && leagueSeasons) {
            const initialSeason = leagueSeasons.find(
                s => s === currentSeason?.setting,
            );

            if (!selectedSeason) {
                dispatch(globalActions.setSelectedSeason(initialSeason));
            }
        }
    }, [leagueSeasons, currentSeason, selectedYear, dispatch]);

    const handleYearChange = (year: number | undefined) => {
        dispatch(globalActions.setSelectedYear(year));
        dispatch(leagueManagementActions.getLeagues(year));
    };

    const handleSeasonChange = (season: string | undefined) => {
        dispatch(globalActions.setSelectedSeason(season));
    };

    const handleAgeLevelChange = (ageLevel: string | undefined) => {
        dispatch(globalActions.setSelectedAgeLevel(ageLevel));
    };

    const carousel1Images: IImage[] =
        photos
            ?.filter(o => o.meta.location?.id === 1 && o.approvalDate !== null)
            .map(p => {
                return { image: p.photo, alt: p.meta.caption || "" };
            }) || [];

    const carousel2Images: IImage[] =
        photos
            ?.filter(o => o.meta.location?.id === 2 && o.approvalDate !== null)
            .map(p => {
                return { image: p.photo, alt: p.meta.caption || "" };
            }) || [];

    const responsive: ResponsiveType = {
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 4,
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 2,
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
        },
    };

    const paginateLeagues = (pageNumber: number) =>
        dispatch(globalActions.setSelectedLeaguePage(pageNumber));

    return (
        <Container data-testid="homePageContent" className="homePage">
            <Row className="py-2 py-lg-5">
                <Col xs={12} lg={6}>
                    <Carousel
                        fade
                        controls={false}
                        indicators={false}
                        interval={5000}
                    >
                        {carousel1Images.map((i: IImage, idx: number) => (
                            <Carousel.Item key={idx}>
                                <img
                                    src={`${UPLOADED_IMAGE_URL}${i.image}`}
                                    alt={i.alt}
                                />
                            </Carousel.Item>
                        ))}
                    </Carousel>
                </Col>
                <Col xs={12} lg={6} className="pt-2 pt-lg-0">
                    <div className="blue-highlight p-4 text-center">
                        <section
                            dangerouslySetInnerHTML={{
                                __html: sanitize(
                                    homePageBlueBoxContent?.setting,
                                ),
                            }}
                        />
                    </div>
                </Col>
            </Row>

            <Row className="py-2 py-lg-5">
                {sponsors?.map(s => {
                    const img = s.logo ? (
                        <img
                            src={`${SPONSOR_LOGO_URL}${s.logo}`}
                            alt={`${s.name} logo`}
                            className="img-fluid"
                        />
                    ) : null;

                    return (
                        <Col xs={6} md={3} key={s.id}>
                            {s.link ? (
                                <Link to={s.link} target="_blank">
                                    {img || s.name}
                                </Link>
                            ) : (
                                img || s.name
                            )}
                        </Col>
                    );
                })}
            </Row>

            <Row className="standings py-5 py-lg-0">
                <Col>
                    <Row className="headerRow">
                        <Col>
                            <h3 className="mx-auto">
                                Standings, Schedules and Playoff Brackets
                            </h3>
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            md={{ span: 10, offset: 1 }}
                            lg={{ span: 6, offset: 3 }}
                        >
                            <Row>
                                <Col xs={4} className="col">
                                    <h4>Year</h4>
                                    <Select
                                        isSearchable={false}
                                        isMulti={false}
                                        className="standingsDropdown"
                                        value={
                                            selectedYear
                                                ? {
                                                      value: selectedYear,
                                                      label: selectedYear.toString(),
                                                  }
                                                : {
                                                      value: 0,
                                                      label: "Select Year",
                                                  }
                                        }
                                        options={leagueYears?.map(y => {
                                            return {
                                                value: y,
                                                label: y.toString(),
                                            };
                                        })}
                                        onChange={y => {
                                            handleYearChange(y?.value);
                                            handleSeasonChange(undefined);
                                            handleAgeLevelChange(undefined);
                                        }}
                                    />
                                </Col>
                                <Col xs={4} className="col">
                                    <h4>Season</h4>
                                    <Select
                                        isSearchable={false}
                                        isMulti={false}
                                        className="standingsDropdown"
                                        value={
                                            selectedSeason
                                                ? {
                                                      value: selectedSeason,
                                                      label: selectedSeason,
                                                  }
                                                : {
                                                      value: "",
                                                      label: "Select Season",
                                                  }
                                        }
                                        options={leagueSeasons.map(s => {
                                            return {
                                                value: s,
                                                label: s,
                                            };
                                        })}
                                        onChange={s => {
                                            handleSeasonChange(s?.label);
                                            s?.label !== selectedSeason &&
                                                handleAgeLevelChange(undefined);
                                        }}
                                    />
                                </Col>
                                <Col xs={4} className="col">
                                    <h4>Age Level</h4>
                                    <Select
                                        key={`${selectedYear}-${selectedSeason}`}
                                        isSearchable={false}
                                        isMulti={false}
                                        className="standingsDropdown"
                                        defaultValue={
                                            selectedAgeLevel
                                                ? {
                                                      value: selectedAgeLevel,
                                                      label: selectedAgeLevel,
                                                  }
                                                : {
                                                      value: "",
                                                      label: "Select Age Level",
                                                  }
                                        }
                                        options={leagueAgeLevels.map(a => {
                                            return {
                                                value: a,
                                                label: a,
                                            };
                                        })}
                                        onChange={a => {
                                            dispatch(
                                                globalActions.setSelectedLeaguePage(
                                                    1,
                                                ),
                                            );
                                            handleAgeLevelChange(a?.value);
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {selectedAgeLevel && (
                        <Row className="leagues">
                            <ul>
                                {leagues
                                    .filter(l => l.year === selectedYear)
                                    .filter(l => l.season === selectedSeason)
                                    .filter(
                                        l => l.ageLevel === selectedAgeLevel,
                                    )
                                    .slice(
                                        selectedLeaguePage * leaguesPerPage -
                                            leaguesPerPage,
                                        selectedLeaguePage * leaguesPerPage,
                                    )
                                    .map(l => {
                                        return (
                                            <li className="league" key={l.id}>
                                                <LeagueStandings
                                                    data={l}
                                                    bordered
                                                    showLinks={true}
                                                />
                                            </li>
                                        );
                                    })}
                            </ul>
                            <Pagination
                                leaguesPerPage={leaguesPerPage}
                                totalLeagues={
                                    leagues
                                        .filter(l => l.year === selectedYear)
                                        .filter(
                                            l => l.season === selectedSeason,
                                        )
                                        .filter(
                                            l =>
                                                l.ageLevel === selectedAgeLevel,
                                        ).length
                                }
                                currentPage={selectedLeaguePage}
                                paginate={paginateLeagues}
                            />
                        </Row>
                    )}
                </Col>
            </Row>

            <Row className="py-2 py-lg-5">
                <Col>
                    <div
                        className="p-4 red-highlight"
                        dangerouslySetInnerHTML={{
                            __html: sanitize(homePageRedBoxContent?.setting),
                        }}
                    />
                </Col>
            </Row>

            <Row className="py-2 py-lg-5">
                <Col id="certifiedUmpires" xs={12} md={6}>
                    <div
                        className="p-4 blue-highlight"
                        dangerouslySetInnerHTML={{
                            __html: sanitize(homePageSplitLeftContent?.setting),
                        }}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <div
                        className="px-4"
                        dangerouslySetInnerHTML={{
                            __html: sanitize(
                                homePageSplitRightContent?.setting,
                            ),
                        }}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <MultiCarousel
                        responsive={responsive}
                        swipeable={false}
                        draggable={false}
                        infinite={true}
                        containerClass="multiCarousel"
                    >
                        {carousel2Images.map((i: IImage, idx: number) => (
                            <div
                                className="carouselImgContainer"
                                key={`carousel-image-${idx}`}
                                onClick={() => {
                                    setModalContent(i);
                                    setModalShow(true);
                                }}
                            >
                                <img
                                    src={`${UPLOADED_IMAGE_URL}${i.image}`}
                                    alt={i.alt}
                                />
                                <span className="rollover">
                                    <MagnifyingGlassIcon />
                                </span>
                            </div>
                        ))}
                    </MultiCarousel>

                    <ImageModal
                        show={modalShow}
                        image={modalContent.image}
                        alt={modalContent.alt}
                        onHide={() => setModalShow(false)}
                    />
                    <h3>Submit your photos to our League Photo Gallery!</h3>
                    <p>
                        Upload your action, fun or team photos{" "}
                        <Link to="/photo-upload">here!</Link>
                    </p>
                </Col>
            </Row>
        </Container>
    );
};

export default HomePage;
